/**
 * Norms Publication Year Utility
 *
 * The norms automation pipeline generates comparison norms every month, always
 * updating the current calendar year's cell as new survey responses arrive. Early
 * in a new year, however, only a handful of schools have completed surveys, so the
 * current year's norm is thin and unrepresentative of the national population.
 *
 * To avoid comparing a school against a statistically meaningless "current year"
 * norm, we do not treat a new calendar year's norms as publishable until enough of
 * the country has surveyed. In the Australian school context, that milestone is the
 * end of Term 1 across all states and territories. Term 1 finishes on different
 * dates in each state, but by early-to-mid April every state has completed it, so we
 * use a single national cutover date (1 May) as a simple, transparent, low-maintenance
 * proxy for "Term 1 has finished everywhere."
 *
 * Before the cutover each year, the published norm year is the PREVIOUS (last
 * completed) calendar year. On or after the cutover, it becomes the current year.
 *
 * This is a client-side display/selection policy only. The pipeline keeps generating
 * every year's norms monthly as designed; this module just decides which year's norms
 * are surfaced to schools as the comparison baseline.
 *
 * @module normsYear
 */

/**
 * National cutover: the point each year at which the new calendar year's norms are
 * considered representative enough to publish as a comparison. Set to 1 May, by which
 * time Term 1 has finished across all Australian states and territories.
 *
 * Stored as 1-based month/day (month 5 = May) for readability.
 */
export const NORMS_PUBLISH_CUTOVER = Object.freeze({ month: 5, day: 1 });

/**
 * The latest calendar year whose norms are considered publishable as of `now`.
 *
 * - Before the cutover date in a given year → last completed year (now.year - 1)
 * - On or after the cutover date → current year (now.year)
 *
 * @param {Date} [now=new Date()] - Reference date (injectable for testing).
 * @returns {number} The published norm year.
 */
export function publishedNormYear(now = new Date()) {
	const year = now.getFullYear();
	// JS months are 0-based; our config is 1-based.
	const cutover = new Date(year, NORMS_PUBLISH_CUTOVER.month - 1, NORMS_PUBLISH_CUTOVER.day);
	return now < cutover ? year - 1 : year;
}

/**
 * Resolve which norm year to compare against, given the year the user/report has
 * selected for their own survey data.
 *
 * A school may be viewing their own current-year survey data before the current
 * year's norms are publishable. In that case we substitute the published (last
 * completed) year's norm as the comparison baseline, so the school still sees their
 * real data but is compared against a representative norm rather than a thin one.
 *
 * Selected years at or below the published year are returned unchanged (historical
 * comparisons are always valid).
 *
 * @param {number} selectedYear - The year selected for the school's own data.
 * @param {Date} [now=new Date()] - Reference date (injectable for testing).
 * @returns {number} The norm year to use for comparison.
 */
export function comparisonNormYear(selectedYear, now = new Date()) {
	const published = publishedNormYear(now);
	if (typeof selectedYear !== "number" || Number.isNaN(selectedYear)) {
		return published;
	}
	return selectedYear > published ? published : selectedYear;
}
